/**
 * Peak Priorities World — content registry types (web mirror).
 *
 * The canonical file lives in the engine at
 * `apps/mountain-world/unity/Assets/StreamingAssets/world.registry.json` and ships inside the
 * WebGL build, so the deployed copy is fetched from the build's StreamingAssets — one source
 * of truth, no sync step. Validate edits with `node scripts/check-world-registry.mjs`.
 * Keep these types in lockstep with the validator and the C# mirror
 * (`apps/mountain-world/unity/Assets/Scripts/World/WorldRegistry.cs`).
 */

export const WORLD_BASE_URL = '/mountain-world';
export const WORLD_LOADER_URL = `${WORLD_BASE_URL}/Build/PeakWorld.loader.js`;
export const WORLD_REGISTRY_URL = `${WORLD_BASE_URL}/StreamingAssets/world.registry.json`;

export interface WorldXZ {
  x: number;
  z: number;
}

export interface WorldSpawn extends WorldXZ {
  facingDeg: number;
}

export type WorldAccent = 'slate' | 'mustard' | 'coral' | 'green';
export type LandKind = 'hub' | 'quarter' | 'ridge' | 'summit' | 'district';
export type LandStatus = 'open' | 'trailhead';

export interface WorldLand {
  id: string;
  name: string;
  kind: LandKind;
  status: LandStatus;
  center: WorldXZ;
  radius: number;
  elevation: number;
  accent: WorldAccent;
  tagline: string;
  productSurfaces: string[];
}

export type WorldSignAction =
  | { type: 'lore' }
  | { type: 'travel'; target: string }
  | { type: 'route'; href: string; label?: string };

export interface WorldSign {
  id: string;
  landId: string;
  position: WorldXZ;
  facingDeg: number;
  title: string;
  body: string;
  accent: WorldAccent;
  action: WorldSignAction;
}

export interface WorldPath {
  id: string;
  from: string;
  to: string;
  style: 'trail' | 'ascent';
  waypoints: WorldXZ[];
}

export interface WorldRegistry {
  version: number;
  world: {
    id: string;
    name: string;
    spawn: WorldSpawn;
    palette: Record<string, string>;
  };
  lands: WorldLand[];
  signs: WorldSign[];
  paths: WorldPath[];
}

/** Engine → host messages (CustomEvent 'peakworld:message' detail). See TECH_PLAN.md §3. */
export type PeakWorldMessage =
  | { type: 'world/ready'; payload: { worldId: string; version: number; lands: number } }
  | { type: 'world/land-entered'; payload: { landId: string; name: string } }
  | { type: 'sign/activated'; payload: { signId: string; landId: string; actionType: string } }
  | { type: 'route/open'; payload: { href: string } }
  | { type: 'world/error'; payload: { message: string } };

/** Fetches the registry from a deployed WebGL build; null when no build is deployed. */
export async function fetchWorldRegistry(): Promise<WorldRegistry | null> {
  try {
    const res = await fetch(WORLD_REGISTRY_URL, { cache: 'no-cache' });
    if (!res.ok) return null;
    return (await res.json()) as WorldRegistry;
  } catch {
    return null;
  }
}

/** True when a WebGL build is deployed under public/mountain-world/. */
export async function isWorldBuildDeployed(): Promise<boolean> {
  try {
    const res = await fetch(WORLD_LOADER_URL, { method: 'HEAD', cache: 'no-cache' });
    return res.ok;
  } catch {
    return false;
  }
}
